import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { signUp, confirmSignUp, signIn } from '@aws-amplify/auth';
import styles from './SignupPopup.module.css';

interface SignupPopupProps {
  show: boolean;
  onHide: () => void;
  onLogin: () => void;
}

const SignupPopup: React.FC<SignupPopupProps> = ({ show, onHide, onLogin }) => {
  const [step, setStep] = useState<'signup' | 'confirm' | 'success'>('signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    team: '',
    password: '',
    confirmPassword: '',
    confirmationCode: ''
  });

  const resetState = () => {
    setStep('signup');
    setLoading(false);
    setError(null);
    setFormData({
      email: '',
      team: '',
      password: '',
      confirmPassword: '',
      confirmationCode: ''
    });
  };

  useEffect(() => {
    if (show) {
      resetState();
    }
  }, [show]);

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('Email and password fields are required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            ...(formData.team && { 'custom:team': formData.team })
          }
        }
      });
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.confirmationCode) {
      setError('Confirmation code is required');
      return;
    }

    setLoading(true);
    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode: formData.confirmationCode
      });
      await signIn({
        username: formData.email,
        password: formData.password
      });
      setStep('success');
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during confirmation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`bi bi-person ${styles.headerIcon}`}></i>
          {step === 'signup' ? 'Create Account' : step === 'confirm' ? 'Confirm Account' : 'Success!'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'signup' ? (
          <Form onSubmit={handleSignup}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Team / Organization
                <span className={styles.optionalLabel}>(optional)</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.team}
                onChange={(e) => setFormData({ ...formData, team: e.target.value })}
                placeholder="Enter your team or organization"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Confirm your password"
              />
            </Form.Group>
            {error && <div className={styles.error}>{error}</div>}
            <Button variant="primary" type="submit" disabled={loading} className="w-100">
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className={`bi bi-person ${styles.buttonIcon}`}></i>
                  Create Account
                </>
              )}
            </Button>
          </Form>
        ) : step === 'confirm' ? (
          <Form onSubmit={handleConfirmation}>
            <p>We've sent a confirmation code to:</p>
            <div className={styles.emailHighlight}>{formData.email}</div>
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Confirmation Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.confirmationCode}
                onChange={(e) => setFormData({ ...formData, confirmationCode: e.target.value })}
                placeholder="Enter confirmation code"
              />
            </Form.Group>
            {error && <div className={styles.error}>{error}</div>}
            <Button variant="primary" type="submit" disabled={loading} className="w-100">
              {loading ? <Spinner animation="border" size="sm" /> : 'Confirm Account'}
            </Button>
          </Form>
        ) : (
          <div className={styles.successContainer}>
            <i className={`bi bi-check-circle-fill ${styles.successIcon}`}></i>
            <h4 className={styles.successTitle}>Account Created Successfully!</h4>
            <p className={styles.successMessage}>
              Your account has been created and you are now logged in.
            </p>
            <Button variant="primary" onClick={onHide} className="w-100">
              Continue
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SignupPopup; 